import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  Divider,
  Chip,
  Link,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import {
  PageSkeleton,
  TextSkeleton,
} from "../../components/common/SkeletonLoaders";
import {
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutofillIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  Analytics as AnalyticsIcon,
} from "@mui/icons-material";
import RichTextEditor from "../../components/outreach/RichTextEditor";
//import MessageTrackingLog from "../../components/outreach/MessageTrackingLog";
import IndividualEmailResults from "../../components/outreach/IndividualEmailResults";
import { IndividualEmailTrackingTable } from "../../components/outreach/IndividualEmailTrackingTable";
import CampaignStatistics from "../../components/outreach/CampaignStatistics";
import CampaignEngagementTrends from "../../components/outreach/CampaignEngagementTrends";
import RefreshButton from "../../components/common/RefreshButton";
import CacheStatusIndicator from "../../components/common/CacheStatusIndicator";
import DataMergeMonitor from "../../components/common/DataMergeMonitor";
import {
  DataSourceIndicator,
  ConnectionStatusBanner,
} from "../../components/common/DataSourceIndicator";
import { PerformanceDashboard } from "../../components/common/PerformanceDashboard";
import {
  OutreachCategory,
  ReactionChannel,
  PlaylistingChannel,
  SpotifyPlaylist,
  MusicBlog,
  Campaign,
  Tag,
  SocialLink,
  SocialPlatform,
} from "../../types/outreach";
import { getOutreachItem } from "../../services/outreach/firestore/crud";
import {
  getTags,
  getTag,
  searchTags,
  createTag as createTagService,
} from "../../services/outreach/firestore/tagService";
import {
  fetchYouTubeChannelDetails,
  YouTubeChannelDetails,
} from "../../services/outreach/integrations/youtube";
import { sendMailgunEmail } from "../../services/outreach/mailgun/messaging";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { generateOutreachMessage } from "../../services/openaiService";
import {
  OutreachDataGrid,
  formatNumber,
} from "../../components/outreach/OutreachDataGrid";
import { useCacheInvalidation } from "../../hooks/useCacheInvalidation";
import { convertPlainUrlsToLinks } from "../../utils/linkUtils";
import type {
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import type {
  IndividualEmailSendResponse,
  EnhancedMessageTrackingData,
} from "../../services/outreach/types";
import { getEnhancedMessageTrackingData } from "../../services/outreach/api/outreachApiClient";
import {
  resolveSocialLinks,
  formatLegacySocialHandles,
} from "../../utils/socialLinks";
import { IoLogoInstagram, IoLogoTwitter, IoLogoYoutube } from "react-icons/io5";
import { SiTiktok, SiFacebook } from "react-icons/si";
import LanguageIcon from "@mui/icons-material/Language";
import LinkIcon from "@mui/icons-material/Link";

// Utility function to extract YouTube video ID from URL
const extractYouTubeVideoId = (url: string): string | null => {
  try {
    // Handle URLs without protocol
    if (!url.startsWith("http")) {
      url = "https://" + url;
    }

    const urlObj = new URL(url);
    const hostname = urlObj.hostname;

    // Check if it's a YouTube URL
    if (!hostname.includes("youtube.com") && !hostname.includes("youtu.be")) {
      return null;
    }

    // Handle different YouTube URL formats
    if (hostname === "youtu.be") {
      // Short URL format: https://youtu.be/VIDEO_ID
      return urlObj.pathname.slice(1);
    } else if (hostname.includes("youtube.com")) {
      // Regular URL format: https://www.youtube.com/watch?v=VIDEO_ID
      const searchParams = urlObj.searchParams;
      return searchParams.get("v");
    }

    return null;
  } catch (error) {
    console.error("Error extracting YouTube video ID:", error);
    return null;
  }
};

// Utility function to get YouTube thumbnail URL (using medium quality for email compatibility)
const getYouTubeThumbnailUrl = (videoId: string): string => {
  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
};

const FALLBACK_TAG_TIMESTAMP = "1970-01-01T00:00:00.000Z";

// Type for the campaign item
type CampaignItem =
  | ReactionChannel
  | PlaylistingChannel
  | SpotifyPlaylist
  | MusicBlog;

interface TagCreationOption {
  inputValue: string;
  name: string;
  isNew: true;
}

type TagOption = Tag | TagCreationOption;

const isTagOption = (option: TagOption | string): option is Tag => {
  return typeof option !== "string" && (option as Tag).id !== undefined;
};

const isCreationOption = (
  option: TagOption | string
): option is TagCreationOption => {
  return (
    typeof option !== "string" &&
    (option as TagCreationOption).isNew !== undefined &&
    (option as TagCreationOption).isNew === true
  );
};

const tagFilterOptions = createFilterOptions<TagOption>();

/**
 * Build a minimal campaign item from denormalized recipient metadata.
 * This avoids fetching full Firestore documents for each recipient on page load.
 * 
 * OPTIMIZATION: Reduces N+1 query pattern (100 recipients → 100 Firestore reads)
 * to just denormalized data already in the campaign document.
 */
const buildMinimalCampaignItem = (
  recipient: import("../../types/outreach").RecipientMetadata
): CampaignItem | null => {
  try {
    const { id, category, name, email, url, subscriberCount, socialLinks, socialHandles } =
      recipient;

    if (!category || !id) {
      return null;
    }

    // Build minimal item based on category
    switch (category) {
      case OutreachCategory.REACTION_CHANNELS: {
        const channel: ReactionChannel = {
          id,
          genre: "Any", // Default genre (can be improved if stored in denormalized data)
          channelName: name || "",
          channelUrl: url || "",
          subscriberCount: subscriberCount?.toString() || "0",
          email: email || "",
          socialHandles: socialHandles || "",
          ...(socialLinks && { socialLinks }),
          // Mark as partially loaded (full details available on-demand)
          lastUpdated: new Date().toISOString(),
        };
        return channel;
      }

      case OutreachCategory.PLAYLISTING_CHANNELS: {
        const channel: PlaylistingChannel = {
          id,
          genre: "Any",
          channelName: name || "",
          channelUrl: url || "",
          subscriberCount: subscriberCount?.toString() || "0",
          email: email || "",
          lastUpdated: new Date().toISOString(),
        };
        return channel;
      }

      case OutreachCategory.SPOTIFY_PLAYLISTS: {
        const playlist: SpotifyPlaylist = {
          id,
          genre: "Any",
          playlistName: name || "",
          playlistUrl: url || "",
          curatorEmail: email || "",
          curatorSocialHandle: "", // Can be improved if stored
          source: "campaign", // Mark as from campaign
          playlistFollowers: subscriberCount || 0,
          playlistLikes: 0, // Can be improved if stored
          lastUpdated: new Date().toISOString(),
        };
        return playlist;
      }

      case OutreachCategory.MUSIC_BLOGS: {
        const blog: MusicBlog = {
          id,
          genre: "Any",
          blogName: name || "",
          contact: email || "",
          blogUrl: url || "",
          monthlyVisits: 0, // Can be improved if stored
          lastUpdated: new Date().toISOString(),
        };
        return blog;
      }

      default:
        return null;
    }
  } catch (e) {
    console.error("Error building minimal campaign item:", e);
    return null;
  }
};

const socialPlatformIcons: Partial<Record<SocialPlatform, React.ReactElement>> = {
  instagram: <IoLogoInstagram />,
  twitter: <IoLogoTwitter />,
  youtube: <IoLogoYoutube />,
  tiktok: <SiTiktok />,
  facebook: <SiFacebook />,
  website: <LanguageIcon fontSize="small" />,
  other: <LinkIcon fontSize="small" />,
};

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`recipient-tabpanel-${index}`}
      aria-labelledby={`recipient-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const CampaignDetailsPage: React.FC = () => {
  const { id, category } = useParams<{ id: string; category: string }>();
  const navigate = useNavigate();

  // Cache invalidation hook
  const { invalidateOnEmailSent, invalidateOnCampaignUpdate } =
    useCacheInvalidation();

  // State for campaign data
  const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);
  const [campaignCategory, setCampaignCategory] = useState<OutreachCategory>(
    OutreachCategory.REACTION_CHANNELS
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [activeRecipientIndex, setActiveRecipientIndex] = useState<number>(0);
  const [existingCampaign, setExistingCampaign] = useState<Campaign | null>(
    null
  );
  const [campaignName, setCampaignName] = useState<string>("");
  const [campaignDescription, setCampaignDescription] = useState<string>("");
  const [campaignMetaInitialized, setCampaignMetaInitialized] =
    useState<boolean>(false);
  const [persistedCreatedAt, setPersistedCreatedAt] = useState<string | null>(
    null
  );

  // Tag management state
  const [tagOptions, setTagOptions] = useState<Tag[]>([]);
  const [tagOptionsLoading, setTagOptionsLoading] = useState<boolean>(false);
  const [tagOptionsError, setTagOptionsError] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [tagInitializationPending, setTagInitializationPending] =
    useState<boolean>(false);
  const [tagCreationLoading, setTagCreationLoading] = useState<boolean>(false);
  const [tagCreationError, setTagCreationError] = useState<string | null>(null);
  const tagCacheRef = useRef<Map<string, Tag>>(new Map());

  const mergeTags = useCallback((existing: Tag[], additions: Tag[]) => {
    const map = new Map<string, Tag>();
    existing.forEach((tag) => {
      map.set(tag.id, tag);
    });
    additions.forEach((tag) => {
      map.set(tag.id, tag);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, []);

  const updateSelectedTagsState = useCallback(
    (tags: Tag[]) => {
      setSelectedTags(tags);
      setTagOptions((prev) => mergeTags(prev, tags));
      tags.forEach((tag) => {
        if (tag?.id) {
          tagCacheRef.current.set(tag.id, tag);
        }
      });
    },
    [mergeTags]
  );

  const findTagByName = useCallback(
    (name: string): Tag | undefined => {
      const normalized = name.trim().toLowerCase();
      if (!normalized) {
        return undefined;
      }

      const fromSelected = selectedTags.find(
        (tag) => tag.name?.toLowerCase() === normalized
      );
      if (fromSelected) {
        return fromSelected;
      }

      const fromOptions = tagOptions.find(
        (tag) => tag.name?.toLowerCase() === normalized
      );
      if (fromOptions) {
        return fromOptions;
      }

      const fromCache = Array.from(tagCacheRef.current.values()).find(
        (tag) => tag.name?.toLowerCase() === normalized
      );
      if (fromCache) {
        return fromCache;
      }

      return undefined;
    },
    [selectedTags, tagOptions]
  );

  useEffect(() => {
    let isMounted = true;

    const loadTagOptions = async () => {
      setTagOptionsLoading(true);
      setTagOptionsError(null);
      try {
        const fetchedTags = await getTags({
          category: OutreachCategory.CAMPAIGNS,
          includeGlobal: true,
        });

        if (!isMounted) {
          return;
        }

        setTagOptions((prev) => mergeTags(prev, fetchedTags));
        fetchedTags.forEach((tag) => {
          tagCacheRef.current.set(tag.id, tag);
        });
      } catch (err) {
        console.error("Error loading campaign tags:", err);
        if (isMounted) {
          setTagOptionsError(
            err instanceof Error ? err.message : "Failed to load campaign tags"
          );
        }
      } finally {
        if (isMounted) {
          setTagOptionsLoading(false);
        }
      }
    };

    loadTagOptions();

    return () => {
      isMounted = false;
    };
  }, [mergeTags]);

  useEffect(() => {
    tagOptions.forEach((tag) => {
      if (tag?.id) {
        tagCacheRef.current.set(tag.id, tag);
      }
    });
  }, [tagOptions]);

  const resolveCampaignTags = useCallback(
    async (campaign: Campaign): Promise<Tag[]> => {
      const resolved: Tag[] = [];
      const seenIds = new Set<string>();
      const idsToLookup = new Set<string>();
      const namesToResolve = new Set<string>();

      const addTag = (tag: Tag | null | undefined) => {
        if (!tag || !tag.id) {
          return;
        }
        if (seenIds.has(tag.id)) {
          return;
        }
        seenIds.add(tag.id);
        resolved.push(tag);
        tagCacheRef.current.set(tag.id, tag);
      };

      if (Array.isArray(campaign.tagIds)) {
        campaign.tagIds
          .map((id) => id?.trim())
          .filter((id): id is string => Boolean(id))
          .forEach((id) => idsToLookup.add(id));
      }

      if (campaign.tagId) {
        idsToLookup.add(campaign.tagId);
      }

      if (campaign.tag && typeof campaign.tag !== "string") {
        addTag(campaign.tag);
      }

      const ids = Array.from(idsToLookup);
      if (ids.length > 0) {
        const fetched = await Promise.all(
          ids.map(async (tagId) => {
            const cached = tagCacheRef.current.get(tagId);
            if (cached) {
              return cached;
            }
            try {
              const tag = await getTag(tagId);
              return tag;
            } catch (err) {
              console.warn(`Unable to fetch tag ${tagId}:`, err);
              return null;
            }
          })
        );
        fetched.forEach((tag) => addTag(tag ?? undefined));
      }

      if (Array.isArray(campaign.tags)) {
        campaign.tags
          .map((name) => name?.trim())
          .filter((name): name is string => Boolean(name))
          .forEach((name) => namesToResolve.add(name));
      }

      if (typeof campaign.tag === "string") {
        const trimmed = campaign.tag.trim();
        if (trimmed) {
          namesToResolve.add(trimmed);
        }
      }

      if (namesToResolve.size > 0) {
        tagOptions.forEach((option) => {
          if (namesToResolve.has(option.name) && !seenIds.has(option.id)) {
            addTag(option);
            namesToResolve.delete(option.name);
          }
        });
      }

      if (namesToResolve.size > 0) {
        for (const name of Array.from(namesToResolve)) {
          try {
            const results = await searchTags(
              name,
              OutreachCategory.CAMPAIGNS,
              5
            );
            const match = results.find(
              (tag) => tag.name.toLowerCase() === name.toLowerCase()
            );
            if (match) {
              addTag(match);
              namesToResolve.delete(name);
            }
          } catch (err) {
            console.warn(`Unable to resolve tag name "${name}":`, err);
          }
        }
      }

      if (namesToResolve.size > 0) {
        namesToResolve.forEach((name) => {
          const safeId = `fallback-${name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")}`;
          if (seenIds.has(safeId)) {
            return;
          }

          const fallback = {
            id: safeId,
            name,
            category: OutreachCategory.CAMPAIGNS,
            createdAt: FALLBACK_TAG_TIMESTAMP,
            updatedAt: FALLBACK_TAG_TIMESTAMP,
            usageCount: 0,
          } as Tag;

          (fallback as Tag & { isFallback?: boolean }).isFallback = true;
          addTag(fallback);
        });
      }

      return resolved;
    },
    [] // CRITICAL FIX: Removed [tagOptions] dependency that was causing infinite loop. tagOptions is captured in closure when needed.
  );

  useEffect(() => {
    if (!existingCampaign) {
      return;
    }

    let isMounted = true;
    setTagInitializationPending(true);

    resolveCampaignTags(existingCampaign)
      .then((resolved) => {
        if (!isMounted) {
          return;
        }
        updateSelectedTagsState(resolved);
      })
      .catch((err) => {
        console.error("Unable to initialize campaign tags:", err);
      })
      .finally(() => {
        if (isMounted) {
          setTagInitializationPending(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [existingCampaign]); // CRITICAL FIX: Only depend on existingCampaign to prevent infinite loop from resolveCampaignTags/updateSelectedTagsState circular dependency

  const handleTagSelectionChange = useCallback(
    (_event: React.SyntheticEvent, newValue: Array<TagOption | string>) => {
      const normalizedOptions = newValue ?? [];
      const existingTags: Tag[] = [];
      const namesToCreate: string[] = [];

      normalizedOptions.forEach((option) => {
        if (!option) {
          return;
        }

        if (typeof option === "string") {
          const match = findTagByName(option);
          if (match) {
            existingTags.push(match);
          } else {
            namesToCreate.push(option);
          }
          return;
        }

        if (isCreationOption(option)) {
          const match = findTagByName(option.inputValue);
          if (match) {
            existingTags.push(match);
          } else {
            namesToCreate.push(option.inputValue);
          }
          return;
        }

        if (isTagOption(option)) {
          existingTags.push(option);
        }
      });

      const baseTags = mergeTags([], existingTags);
      updateSelectedTagsState(baseTags);

      const uniqueNames = Array.from(
        new Set(
          namesToCreate
            .map((name) => name.trim())
            .filter((name) => name.length > 0)
        )
      );

      if (uniqueNames.length === 0) {
        setTagCreationError(null);
        return;
      }

      setTagCreationLoading(true);

      (async () => {
        const createdTags: Tag[] = [];
        const errors: string[] = [];

        for (const name of uniqueNames) {
          try {
            const newTagId = await createTagService({
              name,
              category: OutreachCategory.CAMPAIGNS,
            });
            const createdTag =
              (await getTag(newTagId)) ??
              ({
                id: newTagId,
                name,
                category: OutreachCategory.CAMPAIGNS,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                usageCount: 0,
              } as Tag);
            createdTags.push(createdTag);
            tagCacheRef.current.set(createdTag.id, createdTag);
          } catch (err) {
            const message =
              err instanceof Error
                ? err.message
                : `Unable to create tag "${name}"`;
            console.error(`Unable to create tag "${name}":`, err);
            errors.push(message);
          }
        }

        if (createdTags.length > 0) {
          const combined = mergeTags(baseTags, createdTags);
          updateSelectedTagsState(combined);
        }

        setTagCreationError(errors.length > 0 ? errors[0] : null);
        setTagCreationLoading(false);
      })();
    },
    [findTagByName, mergeTags, updateSelectedTagsState]
  );

  // State for channel details
  const [channelDetails, setChannelDetails] = useState<{
    [id: string]: YouTubeChannelDetails | null;
  }>({});
  const [expandedDescriptions, setExpandedDescriptions] = useState<{
    [id: string]: boolean;
  }>({});
  const [loadingChannelDetails, setLoadingChannelDetails] = useState<{
    [id: string]: boolean;
  }>({});

  // State for message
  const [messageBody, setMessageBody] = useState<string>("");
  const [messageSubject, setMessageSubject] = useState<string>("");
  const [messageTo, setMessageTo] = useState<string>("");
  const [generatingMessage, setGeneratingMessage] = useState<boolean>(false);
  const [sendingEmail, setSendingEmail] = useState<boolean>(false);
  const [latestSendResult, setLatestSendResult] =
    useState<IndividualEmailSendResponse | null>(null);
  const [trackingRecords, setTrackingRecords] = useState<
    EnhancedMessageTrackingData[]
  >([]);
  const [trackingLoading, setTrackingLoading] = useState<boolean>(false);
  const [trackingError, setTrackingError] = useState<string | null>(null);

  // State for video URL
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [videoThumbnail, setVideoThumbnail] = useState<string | null>(null);
  const [fetchingThumbnail, setFetchingThumbnail] = useState<boolean>(false);

  // State for data merge monitoring
  const [showDataMergeMonitor, setShowDataMergeMonitor] =
    useState<boolean>(false);

  const refreshTrackingData = useCallback(async () => {
    if (!id) {
      return;
    }
    setTrackingLoading(true);
    try {
      const response = await getEnhancedMessageTrackingData(id);
      setTrackingRecords(response.data);
      setTrackingError(null);
    } catch (error) {
      console.error("Failed to load individual tracking data", error);
      setTrackingError(
        error instanceof Error ? error.message : "Failed to load tracking data"
      );
    } finally {
      setTrackingLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      refreshTrackingData();
    }
  }, [id, refreshTrackingData]);

  // Determine the category from the URL parameter
  useEffect(() => {
    if (category) {
      switch (category) {
        case "reaction-channels":
          setCampaignCategory(OutreachCategory.REACTION_CHANNELS);
          break;
        case "playlisting-channels":
          setCampaignCategory(OutreachCategory.PLAYLISTING_CHANNELS);
          break;
        case "spotify-playlists":
          setCampaignCategory(OutreachCategory.SPOTIFY_PLAYLISTS);
          break;
        case "music-blogs":
          setCampaignCategory(OutreachCategory.MUSIC_BLOGS);
          break;
        case "campaigns":
          setCampaignCategory(OutreachCategory.CAMPAIGNS);
          break;
        default:
          setCampaignCategory(OutreachCategory.REACTION_CHANNELS);
      }
    }
  }, [category]);

  // Handle video URL change and fetch thumbnail (only for YouTube videos)
  const handleVideoUrlChange = async (url: string) => {
    setVideoUrl(url);
    setVideoThumbnail(null);

    if (!url.trim()) {
      return;
    }

    // Only process YouTube URLs for thumbnail fetching
    const videoId = extractYouTubeVideoId(url);
    if (!videoId) {
      console.log("Not a YouTube URL - skipping thumbnail fetch");
      return;
    }

    console.log(
      "YouTube video detected, fetching thumbnail for video ID:",
      videoId
    );
    setFetchingThumbnail(true);

    try {
      const thumbnailUrl = getYouTubeThumbnailUrl(videoId);

      // Test if the thumbnail exists by trying to load it
      const img = new Image();
      img.onload = () => {
        console.log("YouTube thumbnail loaded successfully");
        setVideoThumbnail(thumbnailUrl);
        setFetchingThumbnail(false);
      };
      img.onerror = () => {
        // Try the medium quality thumbnail if maxresdefault doesn't exist
        const defaultThumbnailUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
        const fallbackImg = new Image();
        fallbackImg.onload = () => {
          console.log("YouTube fallback thumbnail loaded successfully");
          setVideoThumbnail(defaultThumbnailUrl);
          setFetchingThumbnail(false);
        };
        fallbackImg.onerror = () => {
          console.error("Could not load YouTube thumbnail");
          setFetchingThumbnail(false);
        };
        fallbackImg.src = defaultThumbnailUrl;
      };
      img.src = thumbnailUrl;
    } catch (error) {
      console.error("Error fetching YouTube thumbnail:", error);
      setFetchingThumbnail(false);
    }
  };

  // Load channel details
  const loadChannelDetails = useCallback(
    async (campaignId: string, channelUrl: string) => {
      // Guard check using ref-based lookup (doesn't trigger re-renders)
      // This prevents duplicate API calls and infinite loops
      if (campaignId in channelDetails) {
        return; // Already cached or failed previously
      }

      setLoadingChannelDetails((prev) => {
        // If already loading, skip
        if (prev[campaignId]) {
          return prev;
        }
        return { ...prev, [campaignId]: true };
      });

      try {
        const details = await fetchYouTubeChannelDetails(channelUrl);
        setChannelDetails((prev) => ({ ...prev, [campaignId]: details }));
      } catch (err) {
        console.error("Error loading channel details:", err);
        // Set to null on error to prevent infinite retry loop
        setChannelDetails((prev) => ({ ...prev, [campaignId]: null }));
      } finally {
        setLoadingChannelDetails((prev) => ({ ...prev, [campaignId]: false }));
      }
    },
    [] // CRITICAL FIX: Removed dependencies - guard checks are done via state reads, not dependencies
  );

  // Load campaign data
  useEffect(() => {
    const loadCampaigns = async () => {
      if (!id || !campaignCategory) return;

      setLoading(true);
      setLoadingError(null);
      setActionError(null);
      setExistingCampaign(null);
      setCampaignName("");
      setCampaignDescription("");
      setCampaignMetaInitialized(false);
      setPersistedCreatedAt(null);
      updateSelectedTagsState([]);
      setTagCreationError(null);

      try {
        // Handle differently based on whether we're loading a saved campaign or creating a new one
        if (campaignCategory === OutreachCategory.CAMPAIGNS) {
          // We're loading a saved campaign
          const savedCampaign = await getOutreachItem<Campaign>(
            OutreachCategory.CAMPAIGNS,
            id
          );

          if (savedCampaign) {
            setExistingCampaign(savedCampaign);
            // Set the message fields from the saved campaign
            setMessageBody(savedCampaign.messageBody || "");
            setMessageSubject(savedCampaign.messageSubject || "");
            setMessageTo(savedCampaign.messageTo || "");
            const savedName = savedCampaign.name || "";
            const savedDescription = savedCampaign.description || "";
            setCampaignName(savedName);
            setCampaignDescription(savedDescription);
            setCampaignMetaInitialized(
              Boolean(savedName.trim()) || Boolean(savedDescription.trim())
            );
            setPersistedCreatedAt(savedCampaign.createdAt || null);

            // Set video URL if available
            if (savedCampaign.videoUrl) {
              handleVideoUrlChange(savedCampaign.videoUrl);
            }

            // Load the recipient items if available
            if (
              savedCampaign.recipients &&
              savedCampaign.recipients.length > 0
            ) {
              const recipientItems: CampaignItem[] = [];

              console.log(
                "Loading recipients from saved campaign:",
                savedCampaign.recipients
              );

              // First, try to load from localStorage backup if available
              const localStorageBackup = localStorage.getItem(
                `campaign_items_${id}`
              );
              if (localStorageBackup) {
                try {
                  console.log("Found localStorage backup for campaign items");
                  const backupItems = JSON.parse(localStorageBackup);
                  if (Array.isArray(backupItems) && backupItems.length > 0) {
                    console.log(
                      "Using localStorage backup items:",
                      backupItems
                    );
                    setCampaigns(backupItems);

                    // Load channel details for each item from backup
                    for (const campaign of backupItems) {
                      if (
                        campaign.id.includes("reaction") ||
                        campaign.id.includes("playlisting")
                      ) {
                        const channelUrl = (
                          campaign as ReactionChannel | PlaylistingChannel
                        ).channelUrl;
                        if (channelUrl) {
                          loadChannelDetails(campaign.id, channelUrl);
                        }
                      }
                    }

                    // Skip the database loading since we have the backup
                    console.log(
                      "Using backup items, skipping database loading"
                    );
                    return;
                  }
                } catch (e) {
                  console.error("Error parsing localStorage backup:", e);
                  // Continue with database loading if backup fails
                }
              }

              // Check if recipients have denormalized metadata (name field indicates new format)
              const hasEnhancedMetadata =
                Array.isArray(savedCampaign.recipients) &&
                savedCampaign.recipients.length > 0 &&
                typeof savedCampaign.recipients[0] === "object" &&
                "id" in savedCampaign.recipients[0] &&
                "name" in savedCampaign.recipients[0];

              if (hasEnhancedMetadata) {
                console.log("Using denormalized recipient metadata (NEW FORMAT)");
                // Use denormalized data directly - NO Firestore fetches needed!
                // This is the key optimization: avoid N+1 query pattern
                const typedRecipients =
                  savedCampaign.recipients as import("../../types/outreach").RecipientMetadata[];

                // Build lightweight campaign items from denormalized metadata
                for (const recipient of typedRecipients) {
                  try {
                    // Create a minimal item from denormalized data
                    // Only fetch full YouTube details if user selects the recipient (on-demand)
                    const minimalItem = buildMinimalCampaignItem(recipient);
                    if (minimalItem) {
                      console.log(
                        `Using denormalized data for recipient: ${recipient.id}`
                      );
                      recipientItems.push(minimalItem);
                    }
                  } catch (e) {
                    console.error(
                      `Error processing recipient ${recipient.id}:`,
                      e
                    );
                  }
                }

                console.log(
                  `Loaded ${recipientItems.length} recipients from denormalized data (0 Firestore reads)`
                );
                if (recipientItems.length > 0) {
                  setCampaigns(recipientItems);
                }
              } else {
                console.log(
                  "Recipient format is legacy (ID only) - fetching full documents"
                );
                // Legacy format - recipient is just an ID string
                // For backwards compatibility, fetch full documents
                // Cast to string[] since we've verified it's not the enhanced format
                const stringRecipients = savedCampaign.recipients as string[];

                for (const recipientId of stringRecipients) {
                  // Try to load from each category
                  const categories = [
                    OutreachCategory.REACTION_CHANNELS,
                    OutreachCategory.PLAYLISTING_CHANNELS,
                    OutreachCategory.SPOTIFY_PLAYLISTS,
                    OutreachCategory.MUSIC_BLOGS,
                  ];

                  let found = false;
                  for (const cat of categories) {
                    try {
                      console.log(
                        `Trying to load recipient ${recipientId} from category ${cat}`
                      );
                      const item = await getOutreachItem<CampaignItem>(
                        cat,
                        recipientId
                      );
                      if (item) {
                        console.log(
                          `Found recipient ${recipientId} in category ${cat}`
                        );
                        recipientItems.push(item);
                        found = true;
                        break; // Found the item, no need to check other categories
                      }
                    } catch (e) {
                      console.warn(
                        `Error checking category ${cat} for recipient ${recipientId}:`,
                        e
                      );
                      // Ignore errors and try the next category
                    }
                  }

                  if (!found) {
                    console.warn(
                      `Recipient not found in any category: ${recipientId}`
                    );
                  }
                }

                console.log(
                  `Loaded ${recipientItems.length} recipients from Firestore (legacy format)`
                );
                if (recipientItems.length > 0) {
                  setCampaigns(recipientItems);
                } else {
                  console.warn("No recipients could be loaded from the database");
                }
              }
            }
          } else {
            setLoadingError("Campaign not found");
          }
        } else {
          // First, try to get selected items from localStorage
          const storedItemsJson = localStorage.getItem("selectedCampaignItems");
          let campaignItems: CampaignItem[] = [];
          console.log(storedItemsJson);
          if (storedItemsJson) {
            // If we have stored items, use them
            campaignItems = JSON.parse(storedItemsJson);
            setCampaigns(campaignItems);
          } else {
            // Otherwise, just load the single item from the ID in the URL
            const campaignData = await getOutreachItem<CampaignItem>(
              campaignCategory,
              id
            );
            if (campaignData) {
              campaignItems = [campaignData];
              setCampaigns(campaignItems);
            } else {
              setLoadingError("Campaign not found");
            }
          }

          // Load channel details for each item
          for (const campaign of campaignItems) {
            if (
              campaignCategory === OutreachCategory.REACTION_CHANNELS ||
              campaignCategory === OutreachCategory.PLAYLISTING_CHANNELS
            ) {
              const channelUrl = (
                campaign as ReactionChannel | PlaylistingChannel
              ).channelUrl;
              if (channelUrl) {
                loadChannelDetails(campaign.id, channelUrl);
              }
            }
          }
        }
      } catch (err) {
        console.error("Error loading campaigns:", err);
        setLoadingError(
          err instanceof Error ? err.message : "Error loading campaign data"
        );
      } finally {
        setLoading(false);
      }
    };

    loadCampaigns();
  }, [id, campaignCategory, loadChannelDetails, updateSelectedTagsState]);

  const getRecipientDisplayName = useCallback((campaign: CampaignItem) => {
    if ("channelName" in campaign && typeof campaign.channelName === "string") {
      return campaign.channelName;
    }
    if (
      "playlistName" in campaign &&
      typeof campaign.playlistName === "string"
    ) {
      return campaign.playlistName;
    }
    if ("blogName" in campaign && typeof campaign.blogName === "string") {
      return campaign.blogName;
    }
    return "";
  }, []);

  useEffect(() => {
    if (campaignMetaInitialized) {
      return;
    }

    if (existingCampaign) {
      const hasExistingMeta =
        Boolean(existingCampaign.name?.trim()) ||
        Boolean(existingCampaign.description?.trim());
      if (hasExistingMeta) {
        return;
      }
    }

    if (campaigns.length === 0) {
      return;
    }

    if (loading) {
      return;
    }

    const displayNames = campaigns
      .map((item) => getRecipientDisplayName(item))
      .filter((name): name is string => Boolean(name && name.trim()));

    const defaultName =
      displayNames.length > 0
        ? `Campaign for ${displayNames.join(", ")}`
        : "New Campaign";
    const defaultDescription = `Campaign created on ${new Date().toLocaleDateString()}`;

    setCampaignName((current) =>
      current.trim() !== "" ? current : defaultName
    );
    setCampaignDescription((current) =>
      current.trim() !== "" ? current : defaultDescription
    );
    setCampaignMetaInitialized(true);
  }, [
    campaigns,
    campaignMetaInitialized,
    existingCampaign,
    getRecipientDisplayName,
    loading,
  ]);

  // Toggle description expansion
  const toggleDescription = useCallback((campaignId: string) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [campaignId]: !prev[campaignId],
    }));
  }, []);

  // Handle sending email with Mailgun
  const handleSendEmail = async () => {
    if (!messageSubject || !messageBody) {
      setActionError("Subject and message body are required");
      return;
    }

    const effectiveCampaignId = id || `campaign_${Date.now()}`;
    const tagNamesForSend = Array.from(
      new Set(
        selectedTags
          .map((tag) => tag?.name?.trim())
          .filter((name): name is string => Boolean(name))
      )
    );

    setSendingEmail(true);
    setActionError(null);
    setSaveSuccess(false);

    try {
      // Prepare recipients
      const recipients = [];

      // If override address is provided, use it
      if (messageTo && messageTo.trim() !== "") {
        // Split by commas if multiple addresses
        const emails = messageTo.split(",").map((email) => email.trim());

        for (const email of emails) {
          recipients.push({ email, name: "" });
        }
      } else {
        // Otherwise use campaign recipients
        for (const campaign of campaigns) {
          const email = getCampaignEmail(campaign);
          if (email) {
            recipients.push({
              email,
              name: getRecipientDisplayName(campaign),
            });
          }
        }
      }

      if (recipients.length === 0) {
        setActionError("No valid recipients found");
        return;
      }

      // Convert plain URLs to anchor tags for SES click tracking
      const processedBody = convertPlainUrlsToLinks(messageBody);

      // Send email using Mailgun
      const result = await sendMailgunEmail({
        recipients,
        subject: messageSubject,
        body: processedBody,
        overrideToAddress: messageTo,
        campaignId: effectiveCampaignId, // Use the campaign ID from URL params or generate one
        tags: tagNamesForSend,
      });

      setLatestSendResult(result);

      if (result.success) {
        setSaveSuccess(true);
        await persistCampaignSnapshot(effectiveCampaignId, { silent: true });
        if (id) {
          invalidateOnEmailSent(id);
          refreshTrackingData();
        }
        invalidateOnCampaignUpdate(effectiveCampaignId);
      } else {
        const detailedMessages = [
          result.message,
          ...(result.errors ?? []),
        ].filter((msg): msg is string => Boolean(msg && msg.trim()));

        setActionError(
          detailedMessages.length > 0
            ? detailedMessages.join(" ")
            : "Failed to send email"
        );
      }
    } catch (err) {
      console.error("Error sending email:", err);
      setActionError(
        err instanceof Error ? err.message : "Error sending email"
      );
    } finally {
      setSendingEmail(false);
    }
  };

  // Save campaign directly to Firestore
  const saveCampaignToFirestore = async (
    campaignId: string,
    campaignData: any
  ): Promise<{ createdAt: string; updatedAt: string }> => {
    try {
      // Ensure the "data" document exists
      const dataDocRef = doc(db, "outreach", "data");
      await setDoc(dataDocRef, { lastModified: new Date() }, { merge: true });

      // Add timestamps if they don't exist
      const now = new Date().toISOString();
      const createdAt = campaignData.createdAt ?? now;
      const dataWithTimestamps = {
        ...campaignData,
        createdAt,
        updatedAt: now, // Always update the updatedAt timestamp
      };

      // Create or update the campaign document
      const docRef = doc(
        db,
        `outreach/data/${OutreachCategory.CAMPAIGNS}`,
        campaignId
      );
      await setDoc(docRef, dataWithTimestamps);

      return { createdAt, updatedAt: now };
    } catch (error) {
      console.error("Error saving campaign to Firestore:", error);
      throw error;
    }
  };

  const buildCampaignPersistencePayload = () => {
    if (campaigns.length === 0) {
      return null;
    }

    const recipientsWithMetadata = campaigns.map((c) => {
      let category = "";
      let name = "";
      let email = "";
      let url = "";
      let subscriberCount: number | undefined;
      let socialLinks: SocialLink[] | undefined;
      let socialHandles: string | undefined;

      // Determine category and extract denormalized fields
      if (c.id.includes("reaction")) {
        category = OutreachCategory.REACTION_CHANNELS;
      } else if (c.id.includes("playlisting")) {
        category = OutreachCategory.PLAYLISTING_CHANNELS;
      } else if (c.id.includes("spotify")) {
        category = OutreachCategory.SPOTIFY_PLAYLISTS;
      } else if (c.id.includes("blog")) {
        category = OutreachCategory.MUSIC_BLOGS;
      } else if (
        "channelName" in c &&
        ("socialLinks" in c || "socialHandles" in c)
      ) {
        category = OutreachCategory.REACTION_CHANNELS;
      } else if ("channelName" in c) {
        category = OutreachCategory.PLAYLISTING_CHANNELS;
      } else if ("playlistName" in c) {
        category = OutreachCategory.SPOTIFY_PLAYLISTS;
      } else if ("blogName" in c) {
        category = OutreachCategory.MUSIC_BLOGS;
      }

      // Extract denormalized data based on category
      switch (category) {
        case OutreachCategory.REACTION_CHANNELS:
        case OutreachCategory.PLAYLISTING_CHANNELS: {
          const channel = c as ReactionChannel | PlaylistingChannel;
          name = channel.channelName || "";
          email = channel.email || "";
          url = channel.channelUrl || "";
          subscriberCount = channel.subscriberCount
            ? parseInt(channel.subscriberCount)
            : undefined;
          if (
            category === OutreachCategory.REACTION_CHANNELS &&
            "socialLinks" in channel
          ) {
            socialLinks = (channel as ReactionChannel).socialLinks;
          }
          if (
            category === OutreachCategory.REACTION_CHANNELS &&
            "socialHandles" in channel
          ) {
            socialHandles = (channel as ReactionChannel).socialHandles;
          }
          break;
        }
        case OutreachCategory.SPOTIFY_PLAYLISTS: {
          const playlist = c as SpotifyPlaylist;
          name = playlist.playlistName || "";
          email = playlist.curatorEmail || "";
          url = playlist.playlistUrl || "";
          subscriberCount = playlist.playlistFollowers;
          break;
        }
        case OutreachCategory.MUSIC_BLOGS: {
          const blog = c as MusicBlog;
          name = blog.blogName || "";
          email = blog.contact || "";
          url = blog.blogUrl || "";
          break;
        }
      }

      const recipient: import("../../types/outreach").RecipientMetadata = {
        id: c.id,
        category,
        name: name || undefined,
        email: email || undefined,
        url: url || undefined,
        subscriberCount,
        ...(socialLinks && { socialLinks }),
        ...(socialHandles && { socialHandles }),
      };

      return recipient;
    });

    const tagNamesForSave = Array.from(
      new Set(
        selectedTags
          .map((tag) => tag?.name?.trim())
          .filter((name): name is string => Boolean(name))
      )
    );

    const tagIdsForSave = Array.from(
      new Set(
        selectedTags
          .map((tag) =>
            tag?.id && !tag.id.startsWith("fallback-") ? tag.id : null
          )
          .filter((tagId): tagId is string => Boolean(tagId))
      )
    );

    const primaryTagName = tagNamesForSave[0] ?? null;
    const primaryTagId = tagIdsForSave[0] ?? null;

    const recipientNames = campaigns
      .map((item) => getRecipientDisplayName(item))
      .filter((name): name is string => Boolean(name && name.trim()));
    const fallbackCampaignName =
      recipientNames.length > 0
        ? `Campaign for ${recipientNames.join(", ")}`
        : "New Campaign";
    const defaultDescription = `Campaign created on ${new Date().toLocaleDateString()}`;

    const nameToSave = campaignName.trim() || fallbackCampaignName;
    const descriptionToSave =
      campaignDescription.trim() ||
      existingCampaign?.description ||
      defaultDescription;

    return {
      name: nameToSave,
      description: descriptionToSave,
      recipients: recipientsWithMetadata,
      messageBody,
      messageSubject,
      messageTo,
      videoUrl,
      videoThumbnail,
      genre: campaigns[0]?.genre || "Any",
      saved: true,
      tag: primaryTagName,
      tagId: primaryTagId,
      tags: tagNamesForSave,
      tagIds: tagIdsForSave,
      createdAt: persistedCreatedAt ?? existingCampaign?.createdAt ?? null,
    };
  };

  const persistCampaignSnapshot = async (
    campaignId: string,
    { silent = false }: { silent?: boolean } = {}
  ) => {
    const campaignData = buildCampaignPersistencePayload();
    if (!campaignData) {
      if (silent) {
        return null;
      }
      throw new Error("No campaign data available to save");
    }

    try {
      const result = await saveCampaignToFirestore(campaignId, campaignData);
      setPersistedCreatedAt(result.createdAt);
      localStorage.setItem("lastSavedCampaignId", campaignId);
      localStorage.setItem(`campaign_items_${campaignId}`, JSON.stringify(campaigns));
      return result;
    } catch (error) {
      if (silent) {
        console.warn("Failed to persist campaign snapshot for analytics", error);
        return null;
      }
      throw error;
    }
  };

  // Handle save
  const handleSave = async () => {
    if (campaigns.length === 0) return;

    setSaving(true);
    setSaveSuccess(false);
    setActionError(null);

    try {
      const campaignId = id || `campaign_${Date.now()}`;
      const result = await persistCampaignSnapshot(campaignId);
      if (!result) {
        throw new Error("Unable to build campaign payload");
      }

      setSaveSuccess(true);
      invalidateOnCampaignUpdate(campaignId);
    } catch (err) {
      console.error("Error saving campaign:", err);
      setActionError(
        err instanceof Error ? err.message : "Error saving campaign data"
      );
    } finally {
      setSaving(false);
    }
  };

  // Handle autofill
  const handleAutofill = async () => {
    if (campaigns.length === 0) return;

    setGeneratingMessage(true);
    setActionError(null);
    try {
      // Use the active recipient for generating the message
      const campaign = campaigns[activeRecipientIndex];
      let channelData;

      // Get the actual category for the campaign item
      const itemCategory = getItemCategory(campaign);

      // Prepare data for the OpenAI prompt based on campaign type
      if (
        itemCategory === OutreachCategory.REACTION_CHANNELS ||
        itemCategory === OutreachCategory.PLAYLISTING_CHANNELS
      ) {
        const channel = campaign as ReactionChannel | PlaylistingChannel;
        channelData = {
          name: channel.channelName,
          description: channelDetails[campaign.id]?.description,
          subscriberCount: channel.subscriberCount,
          viewCount: channel.viewCount,
          videoCount: channel.videoCount,
          genre: channel.genre,
          type: "youtube" as const,
          videoUrl: videoUrl || undefined,
          videoThumbnail: videoThumbnail || undefined,
        };
      } else if (itemCategory === OutreachCategory.SPOTIFY_PLAYLISTS) {
        const playlist = campaign as SpotifyPlaylist;
        channelData = {
          name: playlist.playlistName,
          subscriberCount: playlist.playlistFollowers,
          genre: playlist.genre,
          type: "spotify" as const,
          videoUrl: videoUrl || undefined,
          videoThumbnail: videoThumbnail || undefined,
        };
      } else if (itemCategory === OutreachCategory.MUSIC_BLOGS) {
        const blog = campaign as MusicBlog;
        channelData = {
          name: blog.blogName,
          genre: blog.genre,
          type: "blog" as const,
          videoUrl: videoUrl || undefined,
          videoThumbnail: videoThumbnail || undefined,
        };
      }

      if (channelData) {
        console.log("Generating message with channel data:", channelData);
        console.log("Video URL:", videoUrl);
        console.log("Video Thumbnail:", videoThumbnail);
        const generatedMessage = await generateOutreachMessage(channelData);
        setMessageBody(generatedMessage);
      }
    } catch (err) {
      console.error("Error generating message:", err);
      setActionError(
        err instanceof Error ? err.message : "Error generating outreach message"
      );
    } finally {
      setGeneratingMessage(false);
    }
  };

  // Determine the actual category for a campaign item
  const getItemCategory = (campaign: CampaignItem): OutreachCategory => {
    // If we're not in the CAMPAIGNS category, use the campaignCategory
    if (campaignCategory !== OutreachCategory.CAMPAIGNS) {
      return campaignCategory;
    }

    // Otherwise, determine the category based on the item's properties
    if (
      "channelName" in campaign &&
      ("socialLinks" in campaign || "socialHandles" in campaign)
    ) {
      return OutreachCategory.REACTION_CHANNELS;
    } else if ("channelName" in campaign) {
      return OutreachCategory.PLAYLISTING_CHANNELS;
    } else if ("playlistName" in campaign) {
      return OutreachCategory.SPOTIFY_PLAYLISTS;
    } else if ("blogName" in campaign) {
      return OutreachCategory.MUSIC_BLOGS;
    }

    // Default to the current campaignCategory if we can't determine
    return campaignCategory;
  };

  // Get campaign URL based on category and index
  const getCampaignUrl = (campaign: CampaignItem): string => {
    const itemCategory = getItemCategory(campaign);

    switch (itemCategory) {
      case OutreachCategory.REACTION_CHANNELS:
      case OutreachCategory.PLAYLISTING_CHANNELS:
        return (campaign as ReactionChannel | PlaylistingChannel).channelUrl || "";
      case OutreachCategory.SPOTIFY_PLAYLISTS:
        return (campaign as SpotifyPlaylist).playlistUrl || "";
      case OutreachCategory.MUSIC_BLOGS:
        return (campaign as MusicBlog).blogUrl || "";
      default:
        return "";
    }
  };

  // Get campaign email based on category and index
  const getCampaignEmail = (campaign: CampaignItem): string => {
    const itemCategory = getItemCategory(campaign);

    switch (itemCategory) {
      case OutreachCategory.REACTION_CHANNELS:
      case OutreachCategory.PLAYLISTING_CHANNELS:
        return (campaign as ReactionChannel | PlaylistingChannel).email || "";
      case OutreachCategory.SPOTIFY_PLAYLISTS:
        return (campaign as SpotifyPlaylist).curatorEmail || "";
      case OutreachCategory.MUSIC_BLOGS:
        return (campaign as MusicBlog).contact || "";
      default:
        return "";
    }
  };

  // Get campaign social links based on category and index
  const getCampaignSocialLinks = (campaign: CampaignItem): SocialLink[] => {
    const itemCategory = getItemCategory(campaign);

    switch (itemCategory) {
      case OutreachCategory.REACTION_CHANNELS:
        return resolveSocialLinks(
          (campaign as ReactionChannel).socialLinks,
          (campaign as ReactionChannel).socialHandles
        );
      default:
        return [];
    }
  };

  // Handle removing a recipient from the list
  const handleRemoveRecipient = useCallback((recipientId: string) => {
    setCampaigns((prevCampaigns) => {
      const updatedCampaigns = prevCampaigns.filter(
        (campaign) => campaign.id !== recipientId
      );

      setActiveRecipientIndex((prevIndex) => {
        if (updatedCampaigns.length === 0) {
          return 0;
        }

        const clampedIndex = Math.min(prevIndex, updatedCampaigns.length - 1);
        return clampedIndex < 0 ? 0 : clampedIndex;
      });

      return updatedCampaigns;
    });
  }, []);

  useEffect(() => {
    setActiveRecipientIndex((prevIndex) => {
      if (campaigns.length === 0) {
        return 0;
      }

      const clampedIndex = Math.min(
        Math.max(prevIndex, 0),
        campaigns.length - 1
      );
      return clampedIndex === prevIndex ? prevIndex : clampedIndex;
    });
  }, [campaigns.length]);

  // Prepare data for the DataGrid based on campaign type
  const prepareDataGridData = (campaigns: CampaignItem[]) => {
    if (!campaigns || campaigns.length === 0) return [];

    const result = [];

    for (const campaign of campaigns) {
      // Determine the actual category for each campaign item
      // This is especially important when campaignCategory is CAMPAIGNS
      let itemCategory = campaignCategory;

      // If we're in the CAMPAIGNS category, determine the actual category for each item
      if (campaignCategory === OutreachCategory.CAMPAIGNS) {
        if (
          "channelName" in campaign &&
          ("socialLinks" in campaign || "socialHandles" in campaign)
        ) {
          itemCategory = OutreachCategory.REACTION_CHANNELS;
        } else if ("channelName" in campaign) {
          itemCategory = OutreachCategory.PLAYLISTING_CHANNELS;
        } else if ("playlistName" in campaign) {
          itemCategory = OutreachCategory.SPOTIFY_PLAYLISTS;
        } else if ("blogName" in campaign) {
          itemCategory = OutreachCategory.MUSIC_BLOGS;
        }
      }

      // Format the data based on the determined category
      switch (itemCategory) {
        case OutreachCategory.REACTION_CHANNELS:
        case OutreachCategory.PLAYLISTING_CHANNELS: {
          const channel = campaign as ReactionChannel | PlaylistingChannel;
          const engagement =
            channel.subscriberCount && channel.likes
              ? (
                  (parseInt(channel.likes) /
                    parseInt(channel.subscriberCount)) *
                  100
                ).toFixed(2) + "%"
              : "N/A";

          // Check if it's a ReactionChannel to include socialHandles
          const isReactionChannel =
            itemCategory === OutreachCategory.REACTION_CHANNELS;

          const socialLinks = isReactionChannel
            ? resolveSocialLinks(
                (campaign as ReactionChannel).socialLinks,
                (campaign as ReactionChannel).socialHandles
              )
            : [];
          const legacySocialHandles = isReactionChannel
            ? (campaign as ReactionChannel).socialHandles &&
              (campaign as ReactionChannel).socialHandles.length > 0
              ? (campaign as ReactionChannel).socialHandles
              : formatLegacySocialHandles(socialLinks)
            : "";

          result.push({
            id: channel.id,
            channelName: channel.channelName,
            channelUrl: channel.channelUrl,
            subscriberCount: channel.subscriberCount,
            viewCount: channel.viewCount || 0,
            videoCount: channel.videoCount || 0,
            likes: channel.likes || 0,
            engagement: engagement,
            genre: channel.genre,
            email: channel.email || "",
            // Only include socialHandles for ReactionChannel
            ...(isReactionChannel && {
              socialHandles: legacySocialHandles,
              socialLinks,
            }),
          });
          break;
        }
        case OutreachCategory.SPOTIFY_PLAYLISTS: {
          const playlist = campaign as SpotifyPlaylist;
          result.push({
            id: playlist.id,
            playlistName: playlist.playlistName,
            playlistUrl: playlist.playlistUrl,
            playlistFollowers: playlist.playlistFollowers,
            playlistLikes: playlist.playlistLikes,
            trackCount: playlist.trackCount || 0,
            genre: playlist.genre,
            curatorEmail: playlist.curatorEmail || "",
            curatorSocialHandle: playlist.curatorSocialHandle || "",
          });
          break;
        }
        case OutreachCategory.MUSIC_BLOGS: {
          const blog = campaign as MusicBlog;
          result.push({
            id: blog.id,
            blogName: blog.blogName,
            blogUrl: blog.blogUrl,
            monthlyVisits: blog.monthlyVisits,
            genre: blog.genre,
            contact: blog.contact || "",
          });
          break;
        }
      }
    }

    return result;
  };

  // Custom cell renderer for channel image
  const renderChannelImageCell = (params: any) => {
    const campaign = campaigns.find((c) => c.id === params.row.id);
    if (!campaign) return null;

    const details = channelDetails[campaign.id];
    if (!details) return null;

    const imageUrl =
      details.thumbnails?.high?.url ||
      details.thumbnails?.medium?.url ||
      details.thumbnails?.default?.url ||
      "";

    if (!imageUrl) return null;

    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <img
          src={imageUrl}
          alt={details.title || "Channel thumbnail"}
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%", // Make image circular
            objectFit: "cover",
          }}
          onError={(e) => {
            console.log("Error loading image, trying fallback");
            const target = e.target as HTMLImageElement;
            const mediumUrl = details.thumbnails?.medium?.url || "";
            const defaultUrl = details.thumbnails?.default?.url || "";

            if (target.src !== mediumUrl && mediumUrl) {
              target.src = mediumUrl;
            } else if (target.src !== defaultUrl && defaultUrl) {
              target.src = defaultUrl;
            }
          }}
        />
      </Box>
    );
  };

  const selectedRecipient = useMemo(() => {
    if (campaigns.length === 0) {
      return null;
    }

    const safeIndex = Math.min(
      Math.max(activeRecipientIndex, 0),
      campaigns.length - 1
    );
    return campaigns[safeIndex] || null;
  }, [campaigns, activeRecipientIndex]);

  const selectedRecipientId = selectedRecipient?.id;

  const dataGridRows = useMemo(
    () => prepareDataGridData(campaigns),
    [campaigns, campaignCategory]
  );

  const effectiveDataGridCategory = useMemo(() => {
    if (
      campaignCategory !== OutreachCategory.CAMPAIGNS ||
      campaigns.length === 0
    ) {
      return campaignCategory;
    }

    const firstItem = campaigns[0];
    if (
      "channelName" in firstItem &&
      ("socialLinks" in firstItem || "socialHandles" in firstItem)
    ) {
      return OutreachCategory.REACTION_CHANNELS;
    }
    if ("channelName" in firstItem) {
      return OutreachCategory.PLAYLISTING_CHANNELS;
    }
    if ("playlistName" in firstItem) {
      return OutreachCategory.SPOTIFY_PLAYLISTS;
    }
    if ("blogName" in firstItem) {
      return OutreachCategory.MUSIC_BLOGS;
    }

    return campaignCategory;
  }, [campaignCategory, campaigns]);

  const handleRecipientRowClick = useCallback(
    (params: GridRowParams) => {
      const clickedIndex = campaigns.findIndex(
        (campaign) => campaign.id === String(params.id)
      );

      if (clickedIndex !== -1) {
        setActiveRecipientIndex(clickedIndex);
      }
    },
    [campaigns]
  );

  const handleRecipientSelectionModelChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      if (!newSelectionModel || newSelectionModel.length === 0) {
        return;
      }

      const [firstSelection] = newSelectionModel;
      const nextId = String(firstSelection);
      const nextIndex = campaigns.findIndex(
        (campaign) => campaign.id === nextId
      );

      if (nextIndex !== -1) {
        setActiveRecipientIndex(nextIndex);
      }
    },
    [campaigns]
  );

  // Render analytics metrics based on category and campaign using DataGrid
  const renderAnalyticsMetrics = () => {
    if (!dataGridRows || dataGridRows.length === 0) return null;

    return (
      <Box sx={{ mb: 3, height: 496 }}>
        <OutreachDataGrid
          category={effectiveDataGridCategory}
          data={dataGridRows}
          loading={false}
          onEdit={() => {}} // No-op since we're just displaying
          onDelete={handleRemoveRecipient}
          rowSelectionModel={
            selectedRecipientId
              ? ([selectedRecipientId] as GridRowSelectionModel)
              : ([] as GridRowSelectionModel)
          }
          onRowSelectionModelChange={handleRecipientSelectionModelChange}
          onRowClick={handleRecipientRowClick}
          selectedRowId={selectedRecipientId}
          checkboxSelection={false}
          // Add custom column for channel image
          customColumns={[
            {
              field: "channelImage",
              headerName: "",
              width: 60,
              renderCell: renderChannelImageCell,
              sortable: false,
              filterable: false,
            },
          ]}
        />
      </Box>
    );
  };

  useEffect(() => {
    if (!selectedRecipient) {
      return;
    }

    const itemCategory = getItemCategory(selectedRecipient);

    // OPTIMIZATION: Only fetch YouTube channel details on-demand when user selects a recipient
    // This prevents the N+1 query pattern from fetching all 100+ channels on page load
    if (
      (itemCategory === OutreachCategory.REACTION_CHANNELS ||
        itemCategory === OutreachCategory.PLAYLISTING_CHANNELS) &&
      selectedRecipient.id
    ) {
      // Check cache directly instead of relying on state
      if (selectedRecipient.id in channelDetails || loadingChannelDetails[selectedRecipient.id]) {
        return; // Already loaded or loading, skip
      }

      const channelUrl = getCampaignUrl(selectedRecipient);

      if (channelUrl) {
        console.log(
          `On-demand fetch: Loading YouTube channel details for selected recipient ${selectedRecipient.id}`
        );
        loadChannelDetails(selectedRecipient.id, channelUrl);
      }
    }
  }, [
    selectedRecipient?.id, // OPTIMIZATION: Only track the ID to detect selection changes
    campaignCategory,
    loadChannelDetails,
  ]); // CRITICAL FIX: Removed channelDetails and loadingChannelDetails from deps to prevent feedback loop. Cache check is done via direct object lookup, not state dependency.

  // Render contact information under the selected recipient
  const renderContactInfoPanel = () => {
    if (!selectedRecipient) {
      return null;
    }

    const email = getCampaignEmail(selectedRecipient);
    const itemCategory = getItemCategory(selectedRecipient);
    const reactionSocialLinks =
      itemCategory === OutreachCategory.REACTION_CHANNELS
        ? getCampaignSocialLinks(selectedRecipient)
        : [];
    const spotifySocialHandle =
      itemCategory === OutreachCategory.SPOTIFY_PLAYLISTS
        ? (selectedRecipient as SpotifyPlaylist).curatorSocialHandle || ""
        : "";
    const shouldShowSocial =
      itemCategory === OutreachCategory.REACTION_CHANNELS ||
      itemCategory === OutreachCategory.SPOTIFY_PLAYLISTS;

    const hasEmail = Boolean(email);
    const hasReactionSocial = reactionSocialLinks.length > 0;
    const hasSpotifySocial = Boolean(spotifySocialHandle);
    const hasSocial = hasReactionSocial || hasSpotifySocial;
    const hasContactDetails = hasEmail || hasSocial;
    const selectedName =
      getRecipientDisplayName(selectedRecipient) || "Selected recipient";

    return (
      <Accordion defaultExpanded sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="subtitle1">Contact Information</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedName}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {hasContactDetails ? (
            <>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="body1" sx={{ mr: 1 }}>
                  Email:
                </Typography>
                {hasEmail ? (
                  <>
                    <Link href={`mailto:${email}`}>{email}</Link>
                    <Tooltip title="Copy to clipboard">
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(email);
                        }}
                        aria-label="Copy email address"
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    N/A
                  </Typography>
                )}
              </Box>
              {shouldShowSocial && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body1" gutterBottom>
                    Social:
                  </Typography>
                  {itemCategory === OutreachCategory.REACTION_CHANNELS ? (
                    hasReactionSocial ? (
                      reactionSocialLinks.map((link, index) => {
                        const icon =
                          socialPlatformIcons[link.platform] ?? (
                            <LinkIcon fontSize="small" />
                          );
                        const chipProps = link.url
                          ? {
                              component: "a" as const,
                              href: link.url,
                              target: "_blank",
                              rel: "noopener noreferrer",
                              clickable: true,
                            }
                          : {};

                        return (
                          <Chip
                            key={`${link.platform}-${link.handle}-${index}`}
                            icon={icon}
                            label={link.label ?? link.handle}
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                            {...chipProps}
                          />
                        );
                      })
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    )
                  ) : hasSpotifySocial ? (
                    <Chip
                      label={spotifySocialHandle}
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      N/A
                    </Typography>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No contact information available.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  // Render channel description
  const renderChannelDescription = () => {
    if (!selectedRecipient || !selectedRecipientId) {
      return null;
    }

    const itemCategory = getItemCategory(selectedRecipient);

    if (
      itemCategory === OutreachCategory.SPOTIFY_PLAYLISTS ||
      itemCategory === OutreachCategory.MUSIC_BLOGS
    ) {
      return (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Channel Description
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Channel description is not available for this recipient type.
          </Typography>
        </Box>
      );
    }

    const details = channelDetails[selectedRecipientId];
    const isExpanded = expandedDescriptions[selectedRecipientId] || true;
    const isLoading = loadingChannelDetails[selectedRecipientId] || false;

    if (isLoading) {
      return (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Channel Description
          </Typography>
          <Box sx={{ height: 100, p: 2 }}>
            <TextSkeleton lines={2} hasTitle={false} />
          </Box>
        </Box>
      );
    }

    if (!details || !details.description) {
      return (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Channel Description
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No channel description available.
          </Typography>
        </Box>
      );
    }

    return (
      <Accordion
        expanded={isExpanded}
        onChange={() => toggleDescription(selectedRecipientId)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Channel Description</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {details.description}
          </Typography>
        </AccordionDetails>
      </Accordion>
    );
  };

  // Render recipient details
  const renderRecipientDetails = () => {
    if (campaigns.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1">No recipients selected</Typography>
        </Box>
      );
    }

    return (
      <Box>
        {/* Analytics Metrics with DataGrid - shows all campaigns */}
        {renderAnalyticsMetrics()}

        {/* Contact Information tied to selected recipient */}
        {renderContactInfoPanel()}

        {/* Channel Description */}
        {renderChannelDescription()}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <PageSkeleton />
      </Container>
    );
  }

  if (loadingError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadingError}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Back to Outreach
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Back to Outreach
        </Button>
        <Box display="flex" alignItems="center" gap={1}>
          {/* <CacheStatusIndicator variant="chip" showDetails />
          <RefreshButton
            campaignId={id}
            variant="icon"
            showAdvancedOptions
            size="medium"
          />
          <Tooltip title="Data Merge Analytics">
            <IconButton
              onClick={() => setShowDataMergeMonitor(true)}
              color="primary"
              size="medium"
            >
              <AnalyticsIcon />
            </IconButton>
          </Tooltip> */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ mr: 1 }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<EmailIcon />}
            onClick={handleSendEmail}
            disabled={sendingEmail}
          >
            {sendingEmail ? "Sending..." : "Send Email"}
          </Button>
        </Box>
      </Box>

      {/* Connection Status Banner */}
      {/*   <ConnectionStatusBanner
        onRetry={() => window.location.reload()}
        showDetails={true}
      /> */}

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {sendingEmail
            ? "Email sent successfully!"
            : "Campaign details saved successfully!"}
        </Alert>
      )}

      {/* Campaign Title */}
      <Typography variant="h6" component="h1" gutterBottom>
        Campaign Details
      </Typography>

      {/* Main Content - Two Column Layout */}
      <Grid container spacing={3}>
        {/* Left Column - Message Body */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            {/* Campaign Name */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Campaign Name
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Enter campaign name"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
              />
            </Box>

            {/* Channel Description */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Channel Description
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                multiline
                minRows={3}
                placeholder="Describe the channel or campaign focus"
                value={campaignDescription}
                onChange={(e) => setCampaignDescription(e.target.value)}
              />
            </Box>

            {/* Message To (Optional) */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                To (Optional - Override Recipients)
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Enter email(s) separated by commas"
                value={messageTo}
                onChange={(e) => setMessageTo(e.target.value)}
                helperText="If provided, this will override the recipient(s) with these email addresses"
              />
            </Box>

            {/* Message Subject */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Subject
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Enter email subject"
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
              />
            </Box>

            {/* Campaign Tags */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Campaign Tags
              </Typography>
              <Autocomplete<TagOption, true, false, true>
                multiple
                freeSolo
                options={tagOptions as TagOption[]}
                value={selectedTags as TagOption[]}
                onChange={handleTagSelectionChange}
                filterSelectedOptions
                getOptionLabel={(option) => {
                  if (typeof option === "string") {
                    return option;
                  }
                  if (isCreationOption(option)) {
                    return option.name;
                  }
                  if (isTagOption(option)) {
                    return option.name;
                  }
                  return "";
                }}
                isOptionEqualToValue={(option, value) => {
                  if (isCreationOption(option) || isCreationOption(value)) {
                    return false;
                  }
                  if (isTagOption(option) && isTagOption(value)) {
                    return option.id === value.id;
                  }
                  return false;
                }}
                filterOptions={(options, params) => {
                  const filtered = tagFilterOptions(options, params);
                  const trimmed = params.inputValue.trim();
                  if (trimmed !== "" && !findTagByName(trimmed)) {
                    filtered.push({
                      inputValue: trimmed,
                      name: `Add "${trimmed}"`,
                      isNew: true,
                    });
                  }
                  return filtered;
                }}
                loading={
                  tagOptionsLoading ||
                  tagInitializationPending ||
                  tagCreationLoading
                }
                renderTags={(value, getTagProps) =>
                  (value as Tag[]).map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select or create tags used to organize this campaign"
                    error={Boolean(tagOptionsError || tagCreationError)}
                    helperText={
                      tagCreationError ??
                      tagOptionsError ??
                      "Tags help track campaigns across outreach tooling"
                    }
                  />
                )}
                noOptionsText={
                  tagOptionsLoading || tagInitializationPending
                    ? "Loading tags..."
                    : "Type to add a new tag"
                }
              />
            </Box>

            {/* Video URL */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Video URL (Optional)
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Enter YouTube video URL to promote (e.g., https://youtu.be/VIDEO_ID)"
                value={videoUrl}
                onChange={(e) => handleVideoUrlChange(e.target.value)}
                helperText="YouTube video URL for promotion - thumbnail will be fetched automatically. Other URLs (Spotify, etc.) are supported but won't fetch thumbnails."
              />
              {fetchingThumbnail && (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary">
                    Fetching YouTube thumbnail...
                  </Typography>
                </Box>
              )}
              {videoThumbnail && (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ mr: 1 }}
                  >
                    YouTube thumbnail ready
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (stored but not displayed)
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Message Body */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1">Message Body</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AutofillIcon />}
                  onClick={handleAutofill}
                  disabled={generatingMessage}
                >
                  {generatingMessage ? "Generating..." : "Autofill"}
                </Button>
              </Box>
              <RichTextEditor
                value={messageBody}
                onChange={setMessageBody}
                height={400}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Right Column - Recipient Details */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recipient Details
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {renderRecipientDetails()}
          </Paper>
        </Grid>
      </Grid>

      {latestSendResult && (
        <Box sx={{ mt: 4 }}>
          <IndividualEmailResults
            result={latestSendResult}
            title="Latest Individual Send"
          />
        </Box>
      )}

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Logs
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {trackingError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {trackingError}
          </Alert>
        )}
        {trackingLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <IndividualEmailTrackingTable
            records={trackingRecords}
            onRefresh={refreshTrackingData}
          />
        )}
      </Box>

      {/* Campaign Performance Section */}
      {id && id !== 'default' && id.length > 10 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Campaign Performance
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Lifetime Statistics */}
          <CampaignStatistics campaignId={id} />

          {/* Engagement Trends Chart */}
          <Box sx={{ mt: 4 }}>
            <CampaignEngagementTrends campaignId={id} />
          </Box>
        </Box>
      )}

      {/* Message Tracking Log - Only show for existing campaigns */}
      {/*} {id && id !== 'default' && id.length > 10 && (
        <MessageTrackingLog
          campaignId={id}
        />
      )} */}

      {/* Data Merge Monitor Dialog */}
      <Dialog
        open={showDataMergeMonitor}
        onClose={() => setShowDataMergeMonitor(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Data Merge Analytics - Campaign {id}</DialogTitle>
        <DialogContent>
          <DataMergeMonitor
            campaignId={id}
            showPerformanceMetrics={true}
            autoRefresh={true}
            refreshInterval={5000}
            onValidationIssues={(result) => {
              console.warn("Data validation issues detected:", result);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDataMergeMonitor(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CampaignDetailsPage;
